import { useEffect, useMemo, useRef, useState } from 'react';
import { CircleCheckIcon } from 'lucide-react';

import type { ProductCategory } from '@/api/hooks/queries/use-get-product-categories-query';
import { Heading1 } from '@/components/common/Heading1';
import { StickyFooterCta } from '@/components/common/StickyFooterCta';
import { FieldError } from '@/components/ui/field';
import { getCategorySelectionCard } from '@/lib/products';
import { cn } from '@/lib/utils';

import { ManageOrderBanner } from './ManageOrderBanner';

type ProductCategoriesProps = {
  categories: ProductCategory[];
  referrerCategoryId?: string;
  selectedCategoryIds: string[];
  onSubmit: (categoryIds: string[]) => void;
};

export function ProductCategories({
  categories,
  referrerCategoryId,
  selectedCategoryIds,
  onSubmit,
}: ProductCategoriesProps) {
  const visibleCategoryIds = useMemo(
    () => new Set(categories.map((category) => category.uuid)),
    [categories]
  );
  const orderedCategories = useMemo(
    () =>
      [...categories].sort((first, second) => {
        if (referrerCategoryId) {
          if (first.uuid === referrerCategoryId) return -1;
          if (second.uuid === referrerCategoryId) return 1;
        }

        return (
          (getCategorySelectionCard(first.label)?.order ?? 0) -
          (getCategorySelectionCard(second.label)?.order ?? 0)
        );
      }),
    [categories, referrerCategoryId]
  );
  const [selectedIds, setSelectedIds] = useState(() => {
    const initialSelectedIds = selectedCategoryIds.filter((categoryId) =>
      visibleCategoryIds.has(categoryId)
    );

    if (referrerCategoryId && visibleCategoryIds.has(referrerCategoryId)) {
      return Array.from(new Set([...initialSelectedIds, referrerCategoryId]));
    }

    return initialSelectedIds;
  });
  const [error, setError] = useState<string | null>(null);
  const errorRef = useRef<HTMLDivElement>(null);
  // A referrer without a card here still lets the patient continue without picking anything
  const hasReferrerCategory = Boolean(referrerCategoryId);

  function toggleCategory(categoryId: string) {
    setError(null);
    setSelectedIds((current) =>
      current.includes(categoryId) && categoryId !== referrerCategoryId
        ? current.filter((selectedId) => selectedId !== categoryId)
        : Array.from(new Set([...current, categoryId]))
    );
  }

  function submitSelection() {
    if (!hasReferrerCategory && selectedIds.length === 0) {
      setError('Please select at least one category');
      return;
    }

    const submitted = selectedIds.filter((categoryId) => visibleCategoryIds.has(categoryId));

    // Insurance benefits scopes products to these ids, so an off-card referrer category has to
    // be carried through even though it has no card to select
    onSubmit(
      referrerCategoryId ? Array.from(new Set([...submitted, referrerCategoryId])) : submitted
    );
  }

  const buttonLabel =
    selectedIds.length > 0
      ? `Continue with ${selectedIds.length} ${
          selectedIds.length === 1 ? 'category' : 'categories'
        } selected`
      : 'Continue';

  useEffect(() => {
    if (error) {
      errorRef.current?.focus();
    }
  }, [error]);

  return (
    <section className="mx-auto flex w-full max-w-3xl flex-col gap-5 pb-28 sm:gap-6 lg:max-w-4xl xl:max-w-5xl">
      <ManageOrderBanner />

      {hasReferrerCategory ? (
        <div>
          <Heading1 className="mb-1 text-[32px] leading-[1.15] sm:mb-1 sm:text-[32px]">
            Do you need any other types of supplies?
          </Heading1>
          <p className="text-base leading-[1.4] text-charcoal-900">
            They could also be covered by insurance.
          </p>
        </div>
      ) : (
        <>
          <CategorySelectionIntro />
          <p className="text-base leading-[1.4] text-charcoal-900">
            Select from the categories below:
          </p>
        </>
      )}

      <div
        className="flex flex-col gap-4 sm:grid sm:grid-cols-2"
        role="group"
        aria-label="Medical supply categories"
      >
        {orderedCategories.map((category) => (
          <ProductCategoryCard
            key={category.uuid}
            category={category}
            isSelected={selectedIds.includes(category.uuid)}
            onToggle={toggleCategory}
          />
        ))}
      </div>

      {error ? (
        <FieldError ref={errorRef} tabIndex={-1} className="scroll-mb-28 outline-none">
          {error}
        </FieldError>
      ) : null}

      <StickyFooterCta
        onClick={submitSelection}
        aria-label={buttonLabel}
        contentClassName="max-w-sm"
        className="sm:h-12 sm:text-base"
      >
        Continue
      </StickyFooterCta>
    </section>
  );
}

function CategorySelectionIntro() {
  return (
    <div className="flex flex-col gap-4 rounded-xl border border-neutral-300 bg-white px-4 py-7 shadow-card">
      <div className="flex items-center gap-2">
        <CircleCheckIcon
          aria-hidden="true"
          className="size-4 shrink-0 fill-spruce-500 text-white"
        />
        <span className="text-sm leading-none font-medium text-charcoal-900">
          Trusted by 50,000+ patients
        </span>
      </div>

      <Heading1 className="mb-0 text-[32px] leading-[1.15] sm:mb-0 sm:text-[32px]">
        You’re minutes away from covered supplies.
      </Heading1>

      <p className="text-base leading-[1.4] text-charcoal-900">
        Over the next few minutes we’ll confirm your insurance eligibility and get you scheduled
        with a telehealth provider if you need a prescription.
      </p>
    </div>
  );
}

type ProductCategoryCardProps = {
  category: ProductCategory;
  isSelected: boolean;
  onToggle: (categoryId: string) => void;
};

function ProductCategoryCard({ category, isSelected, onToggle }: ProductCategoryCardProps) {
  const card = getCategorySelectionCard(category.label);
  const displayLabel = card?.displayLabel ?? category.label;
  const displayDescription = card?.description ?? category.description;
  const images = card?.images ?? [];

  return (
    <button
      type="button"
      aria-pressed={isSelected}
      onClick={() => onToggle(category.uuid)}
      className={cn(
        'flex w-full flex-col gap-3 overflow-hidden rounded-xl border-2 bg-white p-3 text-left shadow-card focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:outline-none',
        isSelected
          ? 'border-neutral-600 bg-neutral-600'
          : 'border-neutral-300 hover:border-neutral-600'
      )}
    >
      <div className="grid grid-cols-3 gap-1">
        {[0, 1, 2].map((slot) => (
          <div
            key={slot}
            className="flex aspect-square min-w-0 items-center justify-center overflow-hidden rounded-lg bg-greige-100"
          >
            {images[slot] ? (
              <img src={images[slot]} alt="" aria-hidden className="h-full w-full object-cover" />
            ) : null}
          </div>
        ))}
      </div>

      <div>
        <span
          className={cn('block text-lg leading-none font-semibold', isSelected && 'text-white')}
        >
          {displayLabel}
        </span>
        {displayDescription ? (
          <span
            className={cn(
              'mt-1 block text-sm leading-[1.4]',
              isSelected ? 'text-white' : 'text-neutral-600'
            )}
          >
            {displayDescription}
          </span>
        ) : null}
      </div>
    </button>
  );
}
